import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MobileRow from './MobileRow';
import useBuildColumns from '../helpers/Columns';
import { UIContext } from '../../../../../context/UIContext';
import { SidekickTablesContext } from '../../helpers/SidekickTablesContext';
import LoadingButton from '../../sharedComponents/LoadingButton';
import DataTable from '../../sharedComponents/DataTable';
import TableWrapper from '../../sharedComponents/TableWrapper';
import MobileTable from '../../sharedComponents/MobileTable';
import { Category } from '../../helpers/types';

const renderRow = (data: Category) => <MobileRow data={data} />;

const CategoriesWrapper = ({
  isLoading,
  openDialog,
  skipCategories,
}: {
  isLoading: boolean;
  openDialog?: () => void;
  skipCategories?: () => void;
}) => {
  const { t } = useTranslation();
  const { isMobile, isMediumDevice } = useContext(UIContext);
  const { isLoading: isAddingCategories, callApiFunction } = useContext(SidekickTablesContext);

  const [resizeToken, setResizeToken] = useState<number>(0);
  const columns = useBuildColumns();

  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => setResizeToken((prevToken) => prevToken + 1), 100);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          mb: 1,
          gap: 1,
          ...(!isMediumDevice && {
            flexDirection: 'column',
            paddingTop: 1,
          }),
        }}
      >
        {skipCategories && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              ...(isMobile && {
                paddingLeft: 1,
              }),
            }}
          >
            <Typography variant="h2">{t('screens.products.sidekick.import.categories')}</Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            gap: 1,
            mb: 1,
            ...(isMobile && {
              justifyContent: 'start',
              flexDirection: 'column',
              marginX: 1,
            }),
          }}
        >
          {skipCategories && (
            <Button
              color="primary"
              variant="outlined"
              disableElevation
              onClick={skipCategories}
              data-qa-id="button-skip-categories"
              aria-label={t('screens.products.sidekick.import.skip')}
            >
              {t('screens.products.sidekick.import.skip')}
            </Button>
          )}
          {openDialog && (
            <LoadingButton
              isLoading={false}
              loadingText=""
              defaultText={t('screens.products.sidekick.import.uploadNewFile')}
              onClick={openDialog}
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              data-qa-id="button-open-csv-dialog"
            />
          )}
          <LoadingButton
            isLoading={isAddingCategories}
            loadingText={t('screens.products.sidekick.import.productsTable.adding')}
            defaultText={t('screens.products.sidekick.import.productsTable.addAllProducts')}
            onClick={() => callApiFunction()}
            variant="contained"
            data-qa-id="button-add-to-catalogue"
          />
        </Box>
      </Box>
      <TableWrapper>
        {isMobile ? (
          <MobileTable<Category>
            isLoading={isLoading}
            renderRow={renderRow}
            emptyStateTitle="screens.products.sidekick.import.noCategories.title"
            emptyStateSubtitle="screens.products.sidekick.import.noCategories.subtitle"
          />
        ) : (
          <DataTable<Category>
            key={resizeToken}
            isLoading={isLoading}
            columns={columns}
            getRowId={(row) => row.id}
            dataQaId="table-categories"
          />
        )}
      </TableWrapper>
    </>
  );
};

export default CategoriesWrapper;
